import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { MessageSquare, Save } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";

export const TelegramSettings = () => {
  const [token, setToken] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const { toast } = useToast();
  const { currentUser } = useAuth();

  const fetchExistingToken = async () => {
    try {
      if (!currentUser?.companyId) {
        console.error('Company ID não encontrado');
        return;
      }

      const { data: telegramSettings, error } = await supabase
        .from('telegram_settings')
        .select('bot_token')
        .eq('company_id', currentUser.companyId)
        .maybeSingle();

      if (error) {
        console.error('Erro ao buscar token:', error);
        throw error;
      }

      if (telegramSettings) {
        setToken(telegramSettings.bot_token);
      }
    } catch (error) {
      console.error('Erro ao buscar token:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar as configurações do Telegram",
        variant: "destructive",
      });
    }
  };

  React.useEffect(() => {
    fetchExistingToken();
  }, [currentUser?.companyId]);

  const handleSave = async () => {
    try {
      setIsLoading(true);

      if (!currentUser?.companyId) {
        throw new Error('Company ID não encontrado');
      }

      const { data: existingSettings, error: fetchError } = await supabase
        .from('telegram_settings')
        .select('id')
        .eq('company_id', currentUser.companyId)
        .maybeSingle();

      if (fetchError) throw fetchError;

      let result;
      
      if (existingSettings) {
        result = await supabase
          .from('telegram_settings')
          .update({ 
            bot_token: token,
            updated_at: new Date().toISOString()
          })
          .eq('company_id', currentUser.companyId);
      } else {
        result = await supabase
          .from('telegram_settings')
          .insert([{ 
            company_id: currentUser.companyId,
            bot_token: token
          }]);
      }

      if (result.error) throw result.error;

      // Atualizar o webhook do Telegram
      const response = await fetch('/api/telegram/set-webhook', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      if (!response.ok) {
        throw new Error('Falha ao configurar webhook do Telegram');
      }

      toast({
        title: "Configuração salva",
        description: "Token do Telegram foi salvo e ativado com sucesso!",
      });
    } catch (error: any) {
      console.error('Erro ao salvar token:', error);
      toast({
        variant: "destructive",
        title: "Erro",
        description: error.message || "Não foi possível salvar o token do Telegram.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center space-x-2">
          <MessageSquare className="h-6 w-6" />
          <CardTitle>Configurações do Telegram</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="space-y-2">
              <Input
                placeholder="Token do Bot do Telegram"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                className="w-full"
                type="password"
              />
              <Button 
                onClick={handleSave}
                className="w-full flex items-center justify-center gap-2"
                disabled={isLoading}
              >
                <Save className="h-4 w-4" />
                {isLoading ? "Salvando..." : "Salvar Configurações"}
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};